// src/components/Navbar.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/e-logo.png";
import {
  FaHome,
  FaUser,
  FaEnvelope,
  FaSearch,
  FaCommentDots,
  FaQuestionCircle,
  FaSignOutAlt,
} from "react-icons/fa";

function resolveHomeRoute(roleRaw) {
  const r = (roleRaw || "").toLowerCase();
  switch (r) {
    case "employer":
      return "/employer/home";
    case "admin":
      return "/admin";
    case "job_seeker":
    case "jobseeker":
      return "/job-seeker/home";
    default:
      return "/";
  }
}

export default function Navbar() {
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const { role, loadingAuth, isAuthed } = useAuth();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const homeRoute = useMemo(() => {
    if (loadingAuth) return "/";
    if (!isAuthed) return "/";
    return resolveHomeRoute(role);
  }, [role, loadingAuth, isAuthed]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      setIsMenuOpen(false);
      navigate("/");
    }
  };

  return (
    <nav className="bg-black text-white px-4 py-2 flex items-center justify-between">
      <button
        type="button"
        className="flex items-center cursor-pointer"
        onClick={() => navigate(homeRoute)}
      >
        <img src={logo} alt="EliteHire Logo" className="h-8 mr-2" />
        <span className="font-bold text-lg text-white">Elite</span>
        <span className="font-bold text-lg text-blue-400">Hire</span>
        <span className="text-sm ml-1">Pro</span>
      </button>

      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setIsMenuOpen((v) => !v)}
          className="ml-auto focus:outline-none"
        >
          <span className="text-white font-medium">Menu</span> ☰
        </button>

        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-gray-50 text-black rounded shadow-md z-50 border border-gray-200">
            <ul className="py-1">
              <li
                onClick={() => {
                  navigate(homeRoute);
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <FaHome /> Home
              </li>

              <li
                onClick={() => {
                  navigate("/profile");
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <FaUser /> My Profile
              </li>

              <li
                onClick={() => {
                  navigate("/inbox");
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <FaEnvelope /> Inbox
              </li>

              <li
                onClick={() => {
                  navigate("/search-users");
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <FaSearch /> Search Users
              </li>

              <li
                onClick={() => {
                  navigate("/feedback");
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <FaCommentDots /> Feedback
              </li>

              <li
                onClick={() => {
                  navigate("/faq");
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <FaQuestionCircle /> FAQ
              </li>

              {!loadingAuth && isAuthed && (
                <li
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-gray-100 cursor-pointer"
                >
                  <FaSignOutAlt /> Log Out
                </li>
              )}

              {!loadingAuth && !isAuthed && (
                <li
                  onClick={() => {
                    navigate("/login");
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <FaUser /> Sign In
                </li>
              )}

              {loadingAuth && (
                <li className="px-4 py-2 text-sm text-gray-600">
                  Checking session...
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
}
